import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { EntityListProvider } from '@backstage/plugin-catalog-react';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { scaffolderPlugin, ScaffolderPage } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { apis } from './apis';
import { CustomSettingsPage } from './components/settings/CustomSettingsPage';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
// Auth API references not needed in this component
import { GcpProjectsPage } from './components/gcp/GcpProjectsPage';
import { GithubRepositories } from './components/github/GithubRepositories';
import { CustomSignInPage } from './components/login/CustomSignInPage';
import { LoginPage } from './components/login/LoginPage';
// Encrypted Secrets - DISABLED
// import { StoredCredentialsPage } from '@backstage/plugin-encrypted-secrets';
import { TerraformCloudPage } from '@internal/plugin-terraform-cloud';
import { FoundationPage } from '@internal/plugin-foundation';
import { VaultSecretsPage } from '@internal/backstage-plugin-vault-secrets';

// Import themes
import { UnifiedThemeProvider } from '@backstage/theme';
import { CssBaseline } from '@material-ui/core';
import { badalLightTheme, badalDarkTheme } from './themes/badalTheme';
import { tdLightTheme, tdDarkTheme } from './themes/tdTheme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import BusinessIcon from '@material-ui/icons/Business';
import { ClaudeFlowPage } from '@internal/plugin-claude-flow';
import { GeminiAgentPage } from '@internal/plugin-gemini-agent';

// Scaffolder Field Extensions
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import {
  GithubRepoPickerExtension,
  GithubFilePickerExtension,
  GithubRepoVariablePickerExtension,
  GithubTeamPickerExtension,
  FlatArrayFieldExtension,
  TerraformVariablesEditorExtension,
  GithubTerraformModuleEditorExtension,
  TenantConfiguratorExtension,
} from './scaffolder/extensions';
import { MockupPage, MockupManagementPage, MockupPreviewPage } from '@internal/plugin-mockup';
import { HomePage } from './components/home/HomePage';
import { PrivacyPolicy, TermsOfService } from './components/legal';

const app = createApp({
  apis,
  themes: [
    {
      id: 'badal-light',
      title: 'Badal Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={badalLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'badal-dark',
      title: 'Badal Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={badalDarkTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'td-light',
      title: 'TD Light',
      variant: 'light',
      icon: <AccountBalanceIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={tdLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'td-dark',
      title: 'TD Dark',
      variant: 'dark',
      icon: <BusinessIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={tdDarkTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <CustomSignInPage {...(props as any)} />,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={
      <EntityListProvider>
        <CatalogIndexPage />
      </EntityListProvider>
    } />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={
      <EntityListProvider>
        <ScaffolderPage />
      </EntityListProvider>
    }>
      <ScaffolderFieldExtensions>
        <GithubRepoPickerExtension />
        <GithubFilePickerExtension />
        <GithubRepoVariablePickerExtension />
        <GithubTeamPickerExtension />
        <FlatArrayFieldExtension />
        <TerraformVariablesEditorExtension />
        <GithubTerraformModuleEditorExtension />
        <TenantConfiguratorExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings/*" element={<CustomSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/gcp-projects" element={<GcpProjectsPage />} />
    <Route path="/github-repositories" element={<GithubRepositories />} />
    {/* Encrypted Secrets - DISABLED */}
    {/* <Route path="/stored-credentials" element={<StoredCredentialsPage />} /> */}
    {/* Login route - uses the custom login page */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/terraform-cloud" element={<TerraformCloudPage />} />
    <Route path="/foundation" element={<FoundationPage />} />
    <Route path="/vault-secrets" element={<VaultSecretsPage />} />
    <Route path="/claude-flow" element={<ClaudeFlowPage />} />
    <Route path="/gemini-agent" element={<GeminiAgentPage />} />
    <Route path="/mockup" element={<MockupPage />} />
    <Route path="/mockup/manage" element={<MockupManagementPage />} />
    <Route path="/mockup/preview/:mockupName/:versionTag?" element={<MockupPreviewPage />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
  </FlatRoutes>
);

// The AppRouter/AppProvider error persists even after version alignment.
// The issue is that app.createRoot() doesn't properly provide the InternalAppContext
// when plugins have mismatched dependencies.
// Using getProvider() as a workaround until all plugin dependencies are fully aligned.

const AppProvider = app.getProvider();

// Public routes that don't require authentication
const publicRoutes = ['/privacy-policy', '/terms-of-service'];

// Check if current path is a public route (before any router is mounted)
const isPublicRoute = () => publicRoutes.includes(window.location.pathname);

// Component to render public pages without authentication
const PublicApp = () => {
  const pathname = window.location.pathname;
  return (
    <UnifiedThemeProvider theme={badalLightTheme}>
      <CssBaseline />
      {pathname === '/privacy-policy' && <PrivacyPolicy />}
      {pathname === '/terms-of-service' && <TermsOfService />}
    </UnifiedThemeProvider>
  );
};

// Main authenticated app
const AuthenticatedApp = () => (
  <AppProvider>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </AppProvider>
);

// Render public or authenticated app based on current path
const App = () => (isPublicRoute() ? <PublicApp /> : <AuthenticatedApp />);

export default App;
